import cn from 'clsx'

import s from './styles.module.scss'

type ColumnBase<T, K extends keyof T> = {
  title: string
  key: K
  width?: number | string
  maxWidth?: number | string
  render?: (value: T[K], row: T) => React.ReactNode
  className?: string
  headerAlign?: 'start' | 'center' | 'end'
}

export type Column<T> = {
  [K in keyof T]: ColumnBase<T, K>
}[keyof T]

type Primitive = string | number | boolean | null | undefined | symbol | bigint | Date
type GroupableKeys<T> = {
  [P in keyof T]-?: T[P] extends Primitive ? P : never
}[keyof T]

type GroupBy<T, K extends GroupableKeys<T>> = {
  field: K
  renderSectionHeader?: (sectionValue: T[K]) => React.ReactNode
  stickyHeader?: boolean
}

type TableProps<T, K extends GroupableKeys<T>> = {
  columns: Column<T>[]
  data: T[]
  rowKey: (row: T) => string | number
  className?: string
  rowClassName?: (row: T) => string | undefined
  bodyClassName?: string
  headerSeparated?: boolean
  rowHeight?: number
  showHeader?: boolean
  rowHoverVariant?: 'scale' | 'colorized'
  groupBy?: K extends never ? never : GroupBy<T, K>
  onRowClick?: (row: T) => void
}

type RowProps<T, K extends GroupableKeys<T>> = Pick<
  TableProps<T, K>,
  'rowClassName' | 'rowHeight' | 'rowHoverVariant' | 'columns'
> & { row: T; onClick: () => void }

export function Table<T, K extends GroupableKeys<T>>({
  columns,
  data,
  rowKey,
  className,
  rowClassName,
  bodyClassName,
  groupBy,
  headerSeparated = true,
  rowHeight = 40,
  showHeader = true,
  rowHoverVariant = 'scale',
  onRowClick,
}: TableProps<T, K>) {
  const content = (() => {
    if (!groupBy?.field) {
      return data.map((row) => (
        <Row
          row={row}
          key={rowKey(row)}
          rowClassName={rowClassName}
          rowHeight={rowHeight}
          rowHoverVariant={rowHoverVariant}
          columns={columns}
          onClick={() => onRowClick?.(row)}
        />
      ))
    }

    const field = groupBy.field as keyof T

    const groups = new Map<T[K], T[]>()
    const order: T[K][] = []

    for (const row of data) {
      const key = row[field] as T[K]
      if (!groups.has(key)) {
        groups.set(key, [])
        order.push(key)
      }
      groups.get(key)!.push(row)
    }

    return order.flatMap((sectionValue) => {
      const rows = groups.get(sectionValue)!

      const headerNode = groupBy.renderSectionHeader?.(sectionValue as any) ?? String(sectionValue ?? '')

      return [
        <div key={`section-${String(sectionValue)}`} className={cn(s.sectionHeader, groupBy.stickyHeader && s.sticky)}>
          {headerNode}
        </div>,
        ...rows.map((row) => (
          <Row
            row={row}
            key={rowKey(row)}
            rowClassName={rowClassName}
            rowHeight={rowHeight}
            rowHoverVariant={rowHoverVariant}
            columns={columns}
            onClick={() => onRowClick?.(row)}
          />
        )),
      ]
    })
  })()

  return (
    <div className={cn(s.table, className)}>
      {showHeader && (
        <div className={cn(s.header, headerSeparated && s.separated)}>
          {columns.map((col) => (
            <div
              key={`${col.key.toString()}${col.title}`}
              className={cn(s.cell, s.headerCell, col.className, col.headerAlign && s[col.headerAlign])}
              style={{ width: col.width, maxWidth: col.maxWidth }}
            >
              {col.title}
            </div>
          ))}
        </div>
      )}

      <div className={cn(s.body, bodyClassName)}>{content}</div>
    </div>
  )
}

const Row = <T, K extends GroupableKeys<T>>({
  row,
  rowHeight,
  rowHoverVariant = 'scale',
  columns,
  rowClassName,
  onClick,
}: RowProps<T, K>) => (
  <div className={cn(s.row, s[rowHoverVariant], rowClassName?.(row))} style={{ height: rowHeight }} onClick={onClick}>
    {columns.map((col) => {
      const rawValue = (row as any)[col.key]
      return (
        <div
          key={`${col.key.toString()}${col.title}`}
          className={cn(s.cell, col.className)}
          style={{ width: col.width, maxWidth: col.maxWidth }}
        >
          {col.render ? col.render(rawValue, row) : rawValue}
        </div>
      )
    })}
  </div>
)
